// The VSCode-style debugger sidebar: three fixed, independently-scrollable panes
// — Locals, Transaction, Threads — every one of them built from the same lazy
// `Tree` widget. It's driven entirely by the trace selection: a (time, thread)
// coordinate yields one debug snapshot, and the object graph under any value is
// resolved lazily from the retained MVCC store via a `GraphView`, so you can
// expand a struct into its fields, a pointer into its target, a collection into
// its entries — to any depth — without the snapshot ever holding the graph.

import type { Store } from "../../src/backend/interp/store.js";
import {
  DebugSnapshot,
  DebugThread,
  DebugThreadState,
  GraphNode,
  nodeForAddr,
  threadView,
  valueNode,
  writeNode,
} from "../../src/backend/interp/debug.js";
import { Tree, TreeNode } from "./Tree.js";

/** One of the sidebar's three panes. */
export type DbgPane = "locals" | "transaction" | "threads";

interface DebuggerPanelProps {
  snapshot: DebugSnapshot | null;
  store: Store | null;
  /** The thread whose locals/txn are shown, and the frame within it. */
  focusedThread: number;
  selectedFrame: number;
  /** Select a specific frame in a thread (from the call-stack tree). */
  onSelectFrame(threadId: number, frameIndex: number): void;
  /** Which panes to render, in order. Defaults to all three (the desktop
   *  sidebar). The mobile layout drills into one pane per screen, so it passes a
   *  single-element list. */
  panes?: readonly DbgPane[];
}

const ALL_PANES: readonly DbgPane[] = ["locals", "transaction", "threads"];

/** The frame's location as a short "file:line" (the program has no file id). */
function frameLoc(source: string | undefined, line: number): string {
  return source ? `${source}:${line}` : `line ${line}`;
}

/** A value row's label: the name (field/local/key) beside its one-line summary. */
function valueLabel(name: string, summary: string): React.ReactNode {
  return (
    <>
      <span className="v-name">{name}</span>
      <span className="v-sum">{summary}</span>
    </>
  );
}

/** How each scheduler state reads in the thread list: the parenthesized word
 *  VSCode puts beside a thread, and the longer "why" behind it on hover. A parked
 *  thread names what it is parked on — the two kinds block on different things and
 *  are woken by different events, which is what you want to know when a run stops
 *  making progress. */
const STATE_LABEL: Record<DebugThreadState, { text: string; title: string }> = {
  running: { text: "(running)", title: "executing — this thread produced the selected event" },
  ready: { text: "(ready)", title: "runnable, waiting for the scheduler to pick it" },
  sleeping: { text: "(sleeping)", title: "in `sleep`; runnable again at a later virtual time" },
  "parked-lock": { text: "(parked: lock)", title: "blocked acquiring its 2PL footprint" },
  "parked-retry": {
    text: "(parked: retry)",
    title: "blocked in `retry`, waiting for a committed write to overlap its read set",
  },
};

/** A thread's state, in the same muted register as a frame's line number rather
 *  than a colour of its own — the pane already codes threads, transactions and the
 *  acting row by colour, and this is context you read, not a signal to spot.
 *  `data-state` carries the raw state for tests to select on. */
function stateLabel(state: DebugThreadState): React.ReactNode {
  const { text, title } = STATE_LABEL[state];
  return (
    <span className="dbg-tstate" data-state={state} title={title}>
      {text}
    </span>
  );
}

/** Turn a lazily-resolved object-graph node into a Tree node. Children stay lazy:
 *  `graph.children` is only invoked when the row is expanded. */
function graphTree(id: string, name: string, graph: GraphNode): TreeNode {
  return {
    id,
    label: valueLabel(name, graph.summary),
    children: graph.children
      ? () => graph.children!().map((c, i) => graphTree(`${i}:${c.label}`, c.label, c.node))
      : undefined,
  };
}

export function DebuggerPanel({
  snapshot,
  store,
  focusedThread,
  selectedFrame,
  onSelectFrame,
  panes = ALL_PANES,
}: DebuggerPanelProps) {
  if (!snapshot || !store) {
    return (
      <div id="debugger">
        <div className="dbg-hint">Select a trace row to inspect the program state at that point.</div>
      </div>
    );
  }

  const focused: DebugThread = snapshot.threads.find((t) => t.id === focusedThread) ?? snapshot.threads[0]!;
  const view = focused ? threadView(store, snapshot, focused) : null;
  const frame = focused?.frames[selectedFrame];

  // --- Locals: the selected frame's bindings, each an expandable value tree. ---
  const localNodes: TreeNode[] =
    frame && view ? frame.locals.map((l) => graphTree(l.name, l.name, valueNode(l.value, view))) : [];

  // --- Transaction: read set and write set of the focused thread's txn. ---
  const txnNodes: TreeNode[] = [];
  if (focused?.txn && view) {
    const txn = focused.txn;
    txnNodes.push({
      id: "reads",
      label: <span className="dbg-group">read set · {txn.reads.length}</span>,
      defaultExpanded: true,
      children: () => txn.reads.map((addr) => graphTree(addr, addr, nodeForAddr(addr, view))),
    });
    txnNodes.push({
      id: "writes",
      label: <span className="dbg-group">write set · {txn.writes.length}</span>,
      defaultExpanded: true,
      children: () => txn.writes.map((w) => graphTree(w.addr, w.addr, writeNode(w.obj, w.addr, view))),
    });
  }

  // --- Threads: every live thread, its call stack nested underneath. ---
  const threadNodes: TreeNode[] = snapshot.threads.map((t) => ({
    id: `T${t.id}`,
    defaultExpanded: true,
    label: (
      <>
        <span className="dbg-tid">T{t.id}</span>
        <span className="dbg-tfn">{t.entryFn}</span>
        {stateLabel(t.state)}
        {t.txn && <span className="dbg-ttxn">txn#{t.txn.id}</span>}
        {t.id === snapshot.actingThread && <span className="dbg-acting" title="produced this event">▸</span>}
      </>
    ),
    // Call stack innermost-first, VSCode-style; each frame selects itself.
    children: () =>
      t.frames
        .map((f, i) => ({ f, i }))
        .reverse()
        .map(({ f, i }) => ({
          id: `f${i}`,
          selected: t.id === focused?.id && i === selectedFrame,
          onSelect: () => onSelectFrame(t.id, i),
          label: (
            <>
              <span className="dbg-fn">{f.fn}</span>
              <span className="dbg-floc">{frameLoc(f.source, f.line)}</span>
            </>
          ),
        })),
  }));

  const paneEls: Record<DbgPane, React.ReactNode> = {
    locals: (
      <section className="dbg-pane" key="locals">
        <header className="dbg-head">Locals</header>
        <div className="dbg-body">
          <Tree
            nodes={localNodes}
            ariaLabel="locals"
            empty={frame ? "no locals in scope" : "no frame selected"}
            focusIsSelection
          />
        </div>
      </section>
    ),
    transaction: (
      <section className="dbg-pane" key="transaction">
        <header className="dbg-head">Transaction{focused?.txn ? ` #${focused.txn.id}` : ""}</header>
        <div className="dbg-body">
          <Tree nodes={txnNodes} ariaLabel="transaction" empty="not in a transaction" focusIsSelection />
        </div>
      </section>
    ),
    threads: (
      <section className="dbg-pane" key="threads">
        <header className="dbg-head">Threads · {snapshot.threads.length}</header>
        <div className="dbg-body">
          <Tree nodes={threadNodes} ariaLabel="threads and call stacks" />
        </div>
      </section>
    ),
  };

  return <div id="debugger">{panes.map((p) => paneEls[p])}</div>;
}
